import { getReferencePrice, referencePriceToSellPrice } from "../economy/itemReferencePrices";
import { Cannon } from "../weapons/cannon.ts";
import { MissileGun } from "../weapons/missileGun.ts";
import { CannonBurst } from "../weapons/cannonBurst.ts";
import { MissileBurst } from "../weapons/missileBurst.ts";
import { DrillLauncher } from "../weapons/drillLauncher.ts";
import { PlasmaGun } from "../weapons/plasmaGun.ts";
import { LandmineWeapon } from "../weapons/landmineWeapon.ts";
import { BarrierGun } from "../weapons/barrierGun.ts";
import { Flamethrower } from "../weapons/flamethrower.ts";
import { LaserGun } from "../weapons/laserGun.ts";
import { GasBombWeapon } from "../weapons/gasBombWeapon.ts";
import { Jetpack } from "../weapons/jetpack.ts";
import { Medikit } from "../items/medikit.ts";
import { Key } from "../items/key.ts";

export interface SellCatalogEntry {
	/** Gold the player receives per single unit sold. */
	unitPrice: number;
	/** Number of ammo/item units per sell-stepper increment. */
	sellPackSize: number;
}

/** Shorthand: sell price derived from the item's reference price. */
const refSell = (key: string) => referencePriceToSellPrice(getReferencePrice(key));

/**
 * Global sell prices for items sold TO a vending machine.
 * Keyed by item key (weapon key for ammo, item key for inventory items).
 * Only finite-ammo weapons and stackable inventory items are sellable.
 *
 * Default unit prices come from ITEM_REFERENCE_PRICES (50% of reference,
 * truncated, min 1). Override with a literal number to hand-tune a single
 * item (e.g. `unitPrice: 3` to undercut the reference-derived price).
 */
const sellCatalog: Record<string, SellCatalogEntry> = {
	// --- Finite-ammo weapons (ammo packs) ---
	[Cannon.key]: { unitPrice: refSell(Cannon.key), sellPackSize: 5 },
	[MissileGun.key]: { unitPrice: refSell(MissileGun.key), sellPackSize: 5 },
	[CannonBurst.key]: { unitPrice: refSell(CannonBurst.key), sellPackSize: 5 },
	[MissileBurst.key]: { unitPrice: refSell(MissileBurst.key), sellPackSize: 1 },
	[DrillLauncher.key]: { unitPrice: refSell(DrillLauncher.key), sellPackSize: 5 },
	[PlasmaGun.key]: { unitPrice: refSell(PlasmaGun.key), sellPackSize: 5 },
	[LandmineWeapon.key]: { unitPrice: refSell(LandmineWeapon.key), sellPackSize: 5 },
	[BarrierGun.key]: { unitPrice: refSell(BarrierGun.key), sellPackSize: 1 },
	[Flamethrower.key]: { unitPrice: refSell(Flamethrower.key), sellPackSize: 1 },
	[LaserGun.key]: { unitPrice: refSell(LaserGun.key), sellPackSize: 1 },
	[GasBombWeapon.key]: { unitPrice: refSell(GasBombWeapon.key), sellPackSize: 1 },
	[Jetpack.key]: { unitPrice: refSell(Jetpack.key), sellPackSize: 1 },

	// --- Inventory items ---
	[Medikit.key]: { unitPrice: refSell(Medikit.key), sellPackSize: 1 },
	[Key.key]: { unitPrice: refSell(Key.key), sellPackSize: 1 },
};

/**
 * Look up the sell price and pack size for an item.
 * Returns null if the item is not sellable (e.g. infinite-ammo weapons).
 */
export function getSellInfo(itemKey: string): SellCatalogEntry | null {
	return sellCatalog[itemKey] ?? null;
}
